import React, { useState } from 'react';
import Layout from './components/Layout';
import ScanScreen from './components/ScanScreen';
import ReadersList from './components/ReadersList';
import CreateReaderForm from './components/CreateReaderForm';
// Mock data types
export type PhysicalReader = {
  serialNumber: string;
  logicalReaderId: string | null;
  doorId: string | null;
  isBlinking?: boolean;
  isConfirmed?: boolean;
};
export type LogicalReader = {
  id: string;
  name: string;
  description: string;
  doorControlUnit: string;
  vgunterNode: string;
  accessFunction: string;
};
export type Door = {
  id: string;
  name: string;
};
export function App() {
  // App states
  const [currentScreen, setCurrentScreen] = useState<'scan' | 'list' | 'create'>('scan');
  const [scannedReaders, setScannedReaders] = useState<PhysicalReader[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  // Mock data for dropdowns
  const [logicalReaders, setLogicalReaders] = useState<LogicalReader[]>([{
    id: 'lr1',
    name: 'Main Entrance Reader',
    description: 'Front door access',
    doorControlUnit: 'DCU-001',
    vgunterNode: 'VN-100',
    accessFunction: 'Standard Access'
  }, {
    id: 'lr2',
    name: 'Office Area Reader',
    description: 'Office section access',
    doorControlUnit: 'DCU-002',
    vgunterNode: 'VN-101',
    accessFunction: 'Office Hours Only'
  }]);
  const [doors, setDoors] = useState<Door[]>([{
    id: 'd1',
    name: 'Main Entrance'
  }, {
    id: 'd2',
    name: 'Office Area'
  }, {
    id: 'd3',
    name: 'Server Room'
  }]);
  // Mock scanning function
  const startScanning = () => {
    setIsScanning(true);
    // Simulate finding devices after a delay
    setTimeout(() => {
      const mockScannedReaders: PhysicalReader[] = [{
        serialNumber: 'KR-12345',
        logicalReaderId: 'lr1',
        doorId: 'd1',
        isBlinking: false,
        isConfirmed: false
      }, {
        serialNumber: 'KR-67890',
        logicalReaderId: null,
        doorId: null,
        isBlinking: false,
        isConfirmed: false
      }, {
        serialNumber: 'KR-24680',
        logicalReaderId: 'lr2',
        doorId: 'd2',
        isBlinking: false,
        isConfirmed: false
      }];
      setScannedReaders(mockScannedReaders);
      setIsScanning(false);
      setCurrentScreen('list');
    }, 3000);
  };
  // Function to assign a logical reader to a physical reader
  const assignLogicalReader = (serialNumber: string, logicalReaderId: string | null) => {
    setScannedReaders(prev => prev.map(reader => reader.serialNumber === serialNumber ? {
      ...reader,
      logicalReaderId
    } : reader));
  };
  // Function to create a new logical reader
  const createLogicalReader = (newReader: Omit<LogicalReader, 'id'>) => {
    const id = `lr${logicalReaders.length + 1}`;
    setLogicalReaders(prev => [...prev, {
      ...newReader,
      id
    }]);
    setCurrentScreen('list');
  };
  // Function to navigate to create screen with context
  const navigateToCreate = (serialNumber?: string) => {
    setCurrentScreen('create');
  };
  // Function to ping a reader and put it in blinking state
  const pingReader = (serialNumber: string) => {
    setScannedReaders(prev => prev.map(reader => reader.serialNumber === serialNumber ? {
      ...reader,
      isBlinking: true,
      isConfirmed: false
    } : reader));
    // Simulate the reader blinking (in a real app this would send a command to the physical device)
    console.log(`Pinging reader ${serialNumber}...`);
  };
  // Function to confirm a reader is blinking
  const confirmReader = (serialNumber: string) => {
    setScannedReaders(prev => prev.map(reader => reader.serialNumber === serialNumber ? {
      ...reader,
      isConfirmed: true
    } : reader));
  };
  // Function to stop a reader from blinking
  const stopBlinking = (serialNumber: string) => {
    setScannedReaders(prev => prev.map(reader => reader.serialNumber === serialNumber ? {
      ...reader,
      isBlinking: false
    } : reader));
  };
  return <Layout title={currentScreen === 'scan' ? 'Scan Keycard Readers' : currentScreen === 'list' ? 'Keycard Readers' : 'Create Logical Reader'} onBack={currentScreen !== 'scan' ? () => setCurrentScreen('scan') : undefined} data-id="element-0">
      {currentScreen === 'scan' && <ScanScreen isScanning={isScanning} onStartScan={startScanning} data-id="element-1" />}
      {currentScreen === 'list' && <ReadersList readers={scannedReaders} logicalReaders={logicalReaders} doors={doors} onAssignReader={assignLogicalReader} onCreateReader={navigateToCreate} onPingReader={pingReader} onConfirmReader={confirmReader} onStopBlinking={stopBlinking} data-id="element-2" />}
      {currentScreen === 'create' && <CreateReaderForm onSubmit={createLogicalReader} doors={doors} data-id="element-3" />}
    </Layout>;
}